import { useCallback, useMemo, useState } from 'react'
import { Table } from '@mantine/core'
import { useIsMutating } from '@tanstack/react-query'

import { TableItem } from '@/modules/employerUsers/pages/components/TableItem/TableItem'
import {
  useUpdateRole,
  useUserItemDelete,
} from '@/modules/employerUsers/pages/components/TableList/TableList.config'
import type { TableListProps } from '@/modules/employerUsers/pages/components/TableList/TableList.types'

import { ConfirmationModal } from '@/components/ConfirmationModal/ConfirmationModal'

import { role } from '@/__generated__/requests'

export function TableList({ items }: TableListProps) {
  const [openedUpdate, setOpenedUpdate] = useState<boolean>(false)
  const [openedDelete, setOpenedDelete] = useState<boolean>(false)
  const [currentUserId, setCurrentUserId] = useState<string>('')

  const isMutating = useIsMutating({
    exact: true,
  })

  const findUser = useCallback(
    (id: string | undefined) => items.find(user => user.id.toString() === id),
    [items],
  )

  const currentUser = useMemo(
    () => findUser(currentUserId),
    [currentUserId, findUser],
  )

  const newRole = currentUser?.roles?.includes(role.EMPLOYEE)
    ? role.EMPLOYER
    : role.EMPLOYEE

  const { updateRole } = useUpdateRole(currentUserId, newRole)
  const { deleteUser } = useUserItemDelete(currentUserId)

  const handleOpenUpdateModal = (id: string | undefined) => {
    if (!id) return

    setCurrentUserId(id)
    setOpenedUpdate(true)
  }

  const handleUpdateRole = () => {
    if (!currentUserId) return

    updateRole()
  }

  const handleOpenDeleteModal = (id: string | undefined) => {
    if (!id) return

    setCurrentUserId(id)
    setOpenedDelete(true)
  }

  const handleDeleteUser = () => {
    if (!currentUserId) return

    deleteUser()
  }

  return (
    <>
      <Table.Tbody>
        {items.length === 0 ? (
          <TableItem empty />
        ) : (
          items.map(user => (
            <TableItem
              key={user.id}
              item={user}
              openDeleteModal={handleOpenDeleteModal}
              openUpdateModal={handleOpenUpdateModal}
            />
          ))
        )}
      </Table.Tbody>

      <ConfirmationModal
        button={{ label: 'Update', variant: 'primary' }}
        description={`Are you sure you want to update role for ${currentUser?.firstName} ${currentUser?.lastName}?`}
        isLoading={!!isMutating}
        onClose={() => setOpenedUpdate(false)}
        onConfirm={handleUpdateRole}
        opened={openedUpdate}
        title="Update role"
      />

      <ConfirmationModal
        button={{ label: 'Delete', variant: 'error' }}
        description={`Are you sure you want to delete ${currentUser?.firstName} ${currentUser?.lastName}?`}
        isLoading={!!isMutating}
        onClose={() => setOpenedDelete(false)}
        onConfirm={handleDeleteUser}
        opened={openedDelete}
        title="Delete user"
      />
    </>
  )
}
